/**
 * Contain scripts highly coupled to the product details page
 */

import axios from 'axios'
import { stringify } from 'qs'

import { openCart } from '../components/cart.js'
import { openModal, initModalTriggers } from "../components/modal"
import { haveSameContents } from '../helpers'
import { initQuantityInputs } from '../components/quantity-input.js'
import { attachCsrfErrorInterceptor } from '../lib/csrf-error-handler.js'
import { getCsrfToken } from '../lib/csrf-token.js'

const client = axios.create({
    transformRequest: [
        function(data) {
            return stringify(data)
        },
    ],
})

// Attach CSRF error interceptor to detect 400 errors from expired sessions
attachCsrfErrorInterceptor(client)

// Identify Request Quote Modal
const raqForm = document.querySelector('.product-quote-modal form')

// Identify Product Description Overflow
const productMore = document.querySelector('[data-product-more]')

// Identify Product Configurator to help control product page logic
const configurator = document.getElementById('configurator')
const configuratorPrintEvent = 'configuratorPrint'
const configuratorSaveEvent = 'configuratorSave'
const configuratorSaveQuoteEvent = 'configuratorSaveQuote'
const configuratorLoginEvent = 'configuratorLogin'
const configuratorDownloadProofEvent = 'configuratorDownloadProof'
const TEMP_CONFIGURATION_PARAM = 'configurator_temp_configuration';

/**
 * Open Reviews Modal if Asked to
 */
if(new URLSearchParams(window.location.search).has('reviews-modal')) {
    openModal('submit-review')
}

/**
 * Expands "More" on Product description overflow
 */
if (productMore) {
    productMore.addEventListener('click', function () {
        document
            .querySelector('[data-product-description]')
            .classList.remove('is-truncated')
        this.remove()
    })
}

/**
 * Request a Quote Form
 */
const updateRaqOptions = (density, color, material, weight, dimensions, fasteners, custom) => {
    if (raqForm) {
        const density = document.querySelector('[name="density"]:checked')?.dataset.name ?? '';
        const color = document.querySelector('[name="color"]:checked')?.dataset.name ?? '';
        const material = document.querySelector('[name="material"]:checked')?.dataset.groupName ?? '';
        const materialGroupColor = document.querySelector('[name="material"]:checked')?.dataset.name ?? '';
        const weight = document.querySelector('[name="material-weight"]:checked')?.dataset.name ?? '';
        const dimensions = document.querySelector('[name="dimensions"]')?.selectedOptions[0].dataset.name ?? '';
        const fasteners = document.querySelector('[name="fasteners"]')?.selectedOptions[0].dataset.name ?? '';
        const custom = document.querySelector('[name="custom"]:checked')?.dataset.name ?? '';
        const quotePrice = document.querySelector('[data-quote-price-element]');
        const raqOptions = raqForm.querySelector('[name="fields[productOptions]"]')
        const raqQuotePrice = raqForm.querySelector('[name="fields[productQuotePrice]"]')
        const valueArray = [];

        if (material) valueArray.push(`Material: ${material}`)
        if (color) valueArray.push(`Color: ${color}`)
        if (materialGroupColor) valueArray.push(`Color: ${materialGroupColor}`)
        if (density) valueArray.push(`Density: ${density}`)
        if (weight) valueArray.push(`Weight: ${weight}`)
        if (dimensions) valueArray.push(`Dimensions: ${dimensions}`)
        if (fasteners) valueArray.push(`Fasteners: ${fasteners}`)
        if (custom) valueArray.push(`Custom: ${custom}`)
        if (quotePrice) {
            
            
            raqQuotePrice.value = quotePrice.innerHTML;
        }

        raqOptions.value = valueArray.join((' | '))
    }
}

/**
 * Initialize Product Form
 * Wrapped in a function so it can be called after Sprig/HTMX loads the sidebar
 */
function initProductForm() {
    console.log('[initProductForm] Starting initialization...');

    // Check for dimension inputs which indicate a configurator product
    const hasDimensionInputs = document.querySelector('[data-width-feet-input]') || document.querySelector('[data-height-feet-input]');

    console.log('[initProductForm] Configurator:', !!configurator, 'Has dimension inputs:', !!hasDimensionInputs);

    // If this is a configurator with dimension inputs, skip standard form init
    // (dimension-based products are handled by initDimensionBasedProduct)
    if (configurator || hasDimensionInputs) {
        console.log('[initProductForm] Skipping standard form init (configurator or dimension-based product)');
        return;
    }

    if (!configurator && !hasDimensionInputs) {
        console.log('[initProductForm] Initializing standard product form...');

        /**
         * Product Form
         */
        const productForm = document.querySelector('[data-product-form]')
        const productOptionGroups = document.querySelectorAll('[data-product-option]')
        const materialOptions = document.querySelectorAll('[data-material-option]')
        const colorOptions = document.querySelectorAll('[data-color-option]')

        console.log('[initProductForm] Found elements:', {
            productForm: !!productForm,
            productOptionGroups: productOptionGroups.length,
            materialOptions: materialOptions.length,
            colorOptions: colorOptions.length
        });

        if (!productForm) {
            console.warn('[initProductForm] Product form not found, aborting initialization');
            return;
        }
    const addToCart = document.querySelector('[data-add-to-cart]')
    let variantInitialId = ''
    if (materialOptions.length) {
        variantInitialId = document.querySelector('[data-initial-variant-id]').dataset.initialVariantId
    }
    const variantSelector = document.querySelector('#purchasableId')
    const variants = document.querySelectorAll('#purchasableId option')
    const allVariants = document.querySelectorAll('#allId option')
    const quantity = productForm.querySelector('[name="qty"]')
    const quoteQuantity = productForm.querySelector('[name="qtyQuote"]')
    const productDetail = document.querySelector('[data-product-detail]')
    //const showQuoteForm = document.querySelector('[data-show-quote-form]')
    const quotePriceContainer = document.querySelector('.product-form__price ');
    const quotePrice = document.querySelector('[data-quote-price-element]');
    const quoteSalePrice = document.querySelector('[data-quote-sale-price-element]');
    const price = productForm.querySelector('[data-price-element]')
    const salePrice = productForm.querySelector('[data-sale-price-element]')
    const separateItemOption = productForm.querySelector('[name="options[separateItem]"]')
    const colorOption = productForm.querySelector('[name="options[color]"]') ?? ''
    const quantityInput = document.querySelector('[data-quantity-input]');
    const lengthInput = document.querySelector('[data-length-input]');
    const priceElement = document.querySelector('[data-price-element]');
    const quoteElement = document.querySelector('[data-quote-price-element]');
    const lengthStorage = document.getElementById('input-length');

    // Pricing
    const formatPrice = (price, quantity) => {
        const formatUSD = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        });
        // To avoid floating-point precision issues, convert to cents, round, then convert back
        const total = Math.round(price * 100 * quantity) / 100;
        return formatUSD.format(total);
    }

    const updatePrice = (purchasableId, variantType, dimensions = 0, useQuoteQty) => {
        // Skip price updates for configurator products - they handle their own pricing
        const hasConfiguratorInputs = document.querySelector('[data-width-feet-input]') || document.querySelector('[data-height-feet-input]');
        if (hasConfiguratorInputs) {
            return;
        }

        let variantPrice = 0;
        let variantSalePrice = 0;
        if (variantType == 'purchasable') {
            const priceElement = variantSelector.querySelector(`[value="${purchasableId}"]`);
            if (priceElement) {
                variantPrice = priceElement.dataset.price;
                variantSalePrice = priceElement.dataset.salePrice;
            }
        } else {
            for (let i = 0; i < allVariants.length; i++) {
                if (allVariants[i].value == purchasableId && allVariants[i].dataset.dimensions == dimensions) {
                    variantPrice = allVariants[i].dataset.price
                    variantSalePrice = allVariants[i].dataset.salePrice
                }
            }
        }
        if (lengthInput) {
            const quantity = parseInt(quantityInput.value) || 0;
            const length = parseInt(lengthInput.value) || 0;
            const basePrice = parseFloat(variantPrice);
            const baseSalePrice = parseFloat(variantSalePrice);
            // To avoid floating-point precision issues, convert to cents, round, then convert back
            const totalPrice = Math.round(basePrice * 100 * quantity * length) / 100;
            const totalSalePrice = Math.round(baseSalePrice * 100 * quantity * length) / 100;

            // Check if product is on sale
            if (basePrice === baseSalePrice) {
                // No sale
                if (price && price.parentElement) {
                    price.parentElement.classList.remove('on-sale')
                }
                if (priceElement) {
                    priceElement.innerHTML = '$' + totalPrice.toFixed(2);
                }
                if (quoteElement) {
                    quoteElement.innerHTML = '$' + totalPrice.toFixed(2);
                }
                if (salePrice) salePrice.innerHTML = '';
            } else {
                // On sale
                if (price && price.parentElement) {
                    price.parentElement.classList.add('on-sale')
                }
                if (priceElement) {
                    priceElement.innerHTML = '$' + totalPrice.toFixed(2);
                }
                if (quoteElement) {
                    quoteElement.innerHTML = '$' + totalPrice.toFixed(2);
                }
                if (salePrice) {
                    salePrice.innerHTML = '$' + totalSalePrice.toFixed(2);
                }
            }

            const selectedVariant = variantSelector.querySelector(`[value="${purchasableId}"]`);
            if (selectedVariant) {
                selectedVariant.dataset.length = length;
            }
        } else {
            if (variantPrice === variantSalePrice) {
                if (price && price.parentElement) {
                    price.parentElement.classList.remove('on-sale')
                }
                if (quotePrice && quotePrice.parentElement) {
                    quotePrice.parentElement.classList.remove('on-sale')
                }
                if (salePrice) salePrice.innerHTML = ''
                if (quoteSalePrice) quoteSalePrice.innerHTML = ''
                if (price) price.innerHTML = formatPrice(variantPrice, quantity.value)
                if (quotePrice) {
                    quotePrice.innerHTML = formatPrice(variantPrice, quantity.value)
                    if (useQuoteQty && quoteQuantity) {
                        quotePrice.innerHTML = formatPrice(variantPrice, quoteQuantity.value)
                    }
                }
                return;
            }
            // Variant is on sale
            if (price && price.parentElement) {
                price.parentElement.classList.add('on-sale')
                price.innerHTML = formatPrice(variantPrice, quantity.value)
            }
            if (quotePrice && quotePrice.parentElement) {
                quotePrice.parentElement.classList.add('on-sale')
                quotePrice.innerHTML = formatPrice(variantPrice, quantity.value)
                if (useQuoteQty && quoteQuantity) {
                    quotePrice.innerHTML = formatPrice(variantPrice, quoteQuantity.value)
                }
            }
            if (salePrice) {
                salePrice.innerHTML = formatPrice(variantSalePrice, quantity.value)
            }
            if (quoteSalePrice) {
                quoteSalePrice.innerHTML = formatPrice(variantSalePrice, quantity.value)
                if (useQuoteQty && quoteQuantity) {
                    quoteSalePrice.innerHTML = formatPrice(variantSalePrice, quoteQuantity.value)
                }
            }
        }
    }

    // Handler for updating which variant is selected based on selected options changing
    const handleOptionChange = (useQuoteQty = false, initialRun = false) => {
        
        // Accessories Fields
        let density = document.querySelector('[name="density"]:checked')?.value ?? '';
        let color = document.querySelector('[name="color"]:checked')?.value ?? '';
        let weight = document.querySelector('[name="material-weight"]:checked')?.value ?? '';

        // All other product types
        let material = document.querySelector('[name="material"]:checked')?.value ?? '';
        let materialColor = document.querySelector('[name="material"]:checked')?.dataset.colorId ?? '';

        // All Products
        let dimensions = document.querySelector('[name="dimensions"]')?.value ?? '';
        let fasteners = document.querySelector('[name="fasteners"]')?.value ?? '';
        let custom = document.querySelector('[name="custom"]:checked')?.value ?? '';

        const quoteNote = document.querySelector('[data-quote-note]');
        const quotPriceNote = document.querySelector('[data-quote-price-note]');

        // Ensure the proper dimensions option is selected 
        if (dimensions) {
            const dimensionsOptions = document.querySelectorAll('[name="dimensions"] option');
            const dimensionsSelect = document.querySelector('[name="dimensions"]');
            dimensionsOptions.forEach(option => {
                if (option.value == dimensions) {
                    let indexToSelect = Array.from(dimensionsSelect).indexOf(option);
                    dimensionsSelect.selectedIndex = indexToSelect;
                }
            });
        }


        // Handle purchaseable variants
        const variantToSelect = [...variants].filter(variant => {
            const selectedOptions = [density, color, material, weight, dimensions, fasteners, custom]
            const variantOptions = [
                variant.dataset.materialDensity,
                variant.dataset.color,
                variant.dataset.material,
                variant.dataset.materialWeight,
                variant.dataset.dimensions,
                variant.dataset.fasteners,
                variant.dataset.customOption
            ]

            return haveSameContents(selectedOptions, variantOptions);
        })

        if (variantToSelect.length) {
            addToCart.removeAttribute('disabled')
            variantSelector.value = variantToSelect[0].value;
            // Trigger change event for configurator products
            variantSelector.dispatchEvent(new Event('change'));
            productDetail.classList.remove('show-quote-form')
            updatePrice(variantSelector.value, 'purchasable', 0, useQuoteQty)
            
            quotePriceContainer.classList.add('hidden');
            if (quoteNote && quoteNote.classList.contains('hidden')) {
                quoteNote.classList.remove('hidden');
            }
            if (quotPriceNote && !quotPriceNote.classList.contains('hidden')) {
                quotPriceNote.classList.add('hidden');
            }
        } else {
            addToCart.setAttribute('disabled', 'disabled')
            productDetail.classList.add('show-quote-form')
            // Find the selected variant from all variants
            const quoteVariant = [...allVariants].filter(variant => {
                const selectedOptions = [density, color, material, weight, dimensions, fasteners, custom]
                const variantOptions = [
                    variant.dataset.materialDensity,
                    variant.dataset.color,
                    variant.dataset.material,
                    variant.dataset.materialWeight,
                    variant.dataset.dimensions,
                    variant.dataset.fasteners,
                    variant.dataset.customOption
                ]
        
                return haveSameContents(selectedOptions, variantOptions);
            });
            if (quoteVariant) {
                if (quotePriceContainer && !quotePriceContainer.classList.contains('hidden')) {
                    if (quoteNote && !quoteNote.classList.contains('hidden')) {
                        quoteNote.classList.add('hidden');
                    }
                    if (quotPriceNote && quotPriceNote.classList.contains('hidden')) {
                        quotPriceNote.classList.remove('hidden');
                    }
                } else {
                    if (quoteNote && quoteNote.classList.contains('hidden')) {
                        quoteNote.classList.remove('hidden');
                    }
                    if (quotPriceNote && !quotPriceNote.classList.contains('hidden')) {
                        quotPriceNote.classList.add('hidden');
                    }
                }
                // Get the option if this variant should show a quote price
                if (quoteVariant[0]) {
                    const showQuotePrice = quoteVariant[0].dataset.showQuotePrice;
                    // Update the quote price
                    updatePrice(quoteVariant[0].value, 'quote', dimensions, useQuoteQty);
                    
                    if (showQuotePrice) {
                    // Show the quote price container
                        quotePriceContainer.classList.remove('hidden'); 
                    } else {
                        // Hide the quote price container
                        quotePriceContainer.classList.add('hidden');
                    } 
                }
                
            }
        }

        // Handle URL variants
        const urlVariantToSelect = [...allVariants].filter(variant => {
            const selectedOptions = [density, color, material, weight, dimensions, fasteners, custom]
            const variantOptions = [
                variant.dataset.materialDensity,
                variant.dataset.color,
                variant.dataset.material,
                variant.dataset.materialWeight,
                variant.dataset.dimensions,
                variant.dataset.fasteners,
                variant.dataset.customOption
            ]

            return haveSameContents(selectedOptions, variantOptions);
        })

        if (urlVariantToSelect.length && !initialRun) {
            const searchParams = new URLSearchParams(location.search);
            searchParams.set('variant', urlVariantToSelect[0].value); // variantToSelect is undefined for non-purchasable products
            if (document.querySelector('[name="material"]:checked')) {
                searchParams.set('color', document.querySelector('[name="material"]:checked').dataset.colorId);
            }
            window.history.replaceState({}, '', `${location.pathname}?${searchParams}`);
        }

        // Disable any custom options that aren't available for the selected material
        const customOptions = document.querySelectorAll('[data-option]');
        const noCustomOptions = document.querySelector('[data-no-options]');
        customOptions.forEach(option => {
            const optionLabel = option.querySelector('label')
            const optionInput = option.querySelector('input')

            if (option.dataset.material.includes(material)) {
                optionInput.removeAttribute('disabled')
                optionLabel.classList.remove('disabled');
            } else {
                optionInput.setAttribute('disabled', 'disabled');
                optionLabel.classList.add('disabled');
                optionInput.checked = false;
            }
        });
        // Check if there are NO active custom options, and if so, display the no options message
        const activeCustomOptions = document.querySelectorAll('[data-option] input:not([disabled])');
        if (activeCustomOptions.length === 0) {
            if (noCustomOptions) {
                noCustomOptions.classList.remove('hidden');
            }
            customOptions.forEach(option => {
                option.classList.add('hidden');
            });
        } else if (noCustomOptions && !noCustomOptions.classList.contains('hidden')) {
            noCustomOptions.classList.add('hidden');
            customOptions.forEach(option => {
                option.classList.remove('hidden');
            });
        }
    }

    // Update Select Options
    const updateSelectOptions = (input, materialGroupId) => {
        const options = input.querySelectorAll('option[data-material-groups]') ?? ''
        const noOption = input.querySelector('option[data-no-options]') ?? ''

        // toggle options
        options.forEach(option => {
            if (option.dataset.materialGroups.includes(materialGroupId)) {
                option.disabled = false
            } else {
                option.disabled = true
            }
        })

        // If the option already selected (e.g. via ?variant= deep-link) is
        // still valid, leave it alone. Otherwise fall through to picking the
        // first available, or showing the no-options placeholder.
        const current = input.options[input.selectedIndex] ?? null
        if (current && !current.disabled && current.dataset.materialGroups) {
            if (noOption) noOption.hidden = true
            input.classList.remove('--no-options')
            return
        }

        let indexToSelect = Array.from(input).indexOf(input.querySelector('option:not([disabled])'))

        if (indexToSelect !== -1) {
            noOption.hidden = true
            input.selectedIndex = indexToSelect
            input.classList.remove('--no-options')
        } else {
            noOption.hidden = false
            input.selectedIndex = 0
            input.classList.add('--no-options')
        }
    }

    // Update Radio Options
    const updateRadioOptions = (input, materialGroupId) => {
        const options = input.querySelectorAll('input[data-material-groups]') ?? ''

        // toggle options
        options.forEach(option => {
            if (option.dataset.materialGroups.includes(materialGroupId)) {
                option.disabled = false
            } else {
                option.disabled = true
            }
        })

        // Preserve the server-rendered or user-selected choice when it's
        // still available — otherwise a deep-linked ?variant= gets clobbered
        // on init by clicking the first remaining radio.
        const alreadyChecked = input.querySelector('input:checked:not([disabled])')
        if (alreadyChecked) {
            input.classList.remove('--no-options')
            return
        }

        // No valid current selection; default to the first available option.
        let indexToSelect = input.querySelector('input:not([disabled])')

        if (indexToSelect) {
            input.classList.remove('--no-options')
            indexToSelect.click()
        } else {
            input.classList.add('--no-options')
        }
    }

    // Update Non-Material and Non-Color Options
    const updateAdditionalOptions = (materialGroupId) => {
        productOptionGroups.forEach(optionGroup => {
            if (optionGroup.dataset.productOption === 'select') {
                const input = optionGroup.querySelector('.custom-select') ?? '' // Gets dimensions, fasteners options

                if (input) {
                    updateSelectOptions(input, materialGroupId)
                }
            } else if (optionGroup.dataset.productOption === 'radio') {
                const input = optionGroup.querySelector('.box-options') ?? '' // Gets density, weight, custom options

                if (input) {
                    updateRadioOptions(input, materialGroupId)
                }
            }
        })
    }

    // Submit
    const handleFormSubmit = async () => {
        const purchasableId = variantSelector.value;

        if(purchasableId) {
            const csrfToken = await getCsrfToken({ force: true });

            // Build options object conditionally
            const options = {};
            if (separateItemOption) {
                options.separateItem = separateItemOption.value;
            }
            if (colorOption && colorOption.value) {
                options.color = colorOption.value;
            }
            if (lengthInput && lengthInput.value) {
                options.purchasableLength = lengthInput.value;
            }

            const response = await client.post('', {
                action: "commerce/cart/update-cart",
                purchasableId,
                options,
                qty: quantity.value
            }, {
                headers: {
                    'X-CSRF-Token': csrfToken
                }
            }).catch(error => console.error(error))
        }
    }

    // Event Listeners
    console.log('[initProductForm] Attaching event listeners to:', {
        materialOptions: materialOptions.length,
        productOptionGroups: productOptionGroups.length,
        colorOptions: colorOptions.length
    });

    materialOptions.forEach((optionGroup, index) => {
        console.log('[initProductForm] Attaching material option listener', index);
        optionGroup.addEventListener('change', (event) => {
            console.log('[Material Change] Material option changed');
            const groupId = event.target.dataset.group
            const swatchGroups = document.querySelectorAll('[data-material-group]')
            const selectedColor = document.querySelector('[name="material"]:checked') ?? ''
            // de-select any selected colors
            if (selectedColor) {
                selectedColor.checked = false
            }
            // toggle colors displayed
            swatchGroups.forEach(groupDiv => {
                if(groupDiv.dataset.materialGroup == groupId){
                    groupDiv.classList.remove("--is-hidden")
                    document.querySelector(`.swatch-options[data-material-group="${groupId}"]`).querySelectorAll('input')[0].click()
                } else {
                    groupDiv.classList.add("--is-hidden")
                }
            })
            updateAdditionalOptions(groupId)
            handleOptionChange(false, false)
        })
    })

    productOptionGroups.forEach((optionGroup, index) => {
        console.log('[initProductForm] Attaching product option listener', index);
        optionGroup.addEventListener('change', (event) => {
            console.log('[Option Change] Product option changed');
            handleOptionChange(false, false)
        })
    })

    colorOptions.forEach((colorOption, index) => {
        console.log('[initProductForm] Attaching color option listener', index);
        const titleEl = colorOption.querySelector('[data-option-title]')
        const noteInput = document.querySelector('input[data-selected-color]')
        colorOption.addEventListener('click', (event) => {
            console.log('[Color Change] Color option clicked');
            // Find the swatch option container, then get the input within it
            const swatchOption = event.target.closest('.swatch-options__option');
            const input = swatchOption?.querySelector('input[data-name]');
            const color = input?.dataset.name;
            if (color) {
                if (titleEl) {
                    titleEl.innerHTML = color;
                }
                noteInput.value = color;
            }
        })
    })

    // Run option change once to set up for first selected variant
    if (productOptionGroups.length) {
        handleOptionChange(false, true)
    }

    /**
     * Update price on quantity and length change
     */
    if (quantity && !lengthInput) {
        quantity.addEventListener('change', () => {
            updatePrice(variantSelector.value, 'purchasable', 0, false)
            

            // Remove error notice once corrected
            if(quantity.checkValidity()) {
                quantity.parentElement.classList.remove('has-error')
            }
        })

        quantity.addEventListener('invalid', () => {
            quantity.parentElement.classList.add('has-error')
        })
    }

    if (quoteQuantity && !lengthInput) {
        quoteQuantity.addEventListener('change', () => {
            
            handleOptionChange(true, false)

            // Remove error notice once corrected
            if(quoteQuantity.checkValidity()) {
                quoteQuantity.parentElement.classList.remove('has-error')
            }
        })

        quoteQuantity.addEventListener('invalid', () => {
            quoteQuantity.parentElement.classList.add('has-error')
        })
    }

    if (lengthInput && quantityInput) {
        if (variantSelector.value == '') {
            handleOptionChange(true, true)
        } else {
            updatePrice(variantSelector.value, 'purchasable', 0, false);
        }
        
        if(lengthStorage) {
            lengthStorage.value = lengthInput.value;
        }
        lengthInput.addEventListener('change', function(){
            if (variantSelector.value == '') {
                handleOptionChange(true, false)
            } else {
                updatePrice(variantSelector.value, 'purchasable', 0, false);
            }
            
            if(lengthStorage) {
                lengthStorage.value = lengthInput.value;
            }
        });
        quantityInput.addEventListener('change', function(){
            if (variantSelector.value == '') {
                handleOptionChange(true, false)
            } else {
                updatePrice(variantSelector.value, 'purchasable', 0, false);
            }
            
        });
    } 


    /**
     * Add to Cart
     */
    // Check if we've already attached the listener (prevent duplicates)
    if (!productForm.dataset.submitHandlerAttached) {
        productForm.dataset.submitHandlerAttached = 'true';

        productForm.addEventListener('submit', async function(event) {
            event.preventDefault();
            addToCart.setAttribute('disabled', 'disabled')
            addToCart.innerHTML = 'Adding...'
            await handleFormSubmit()
            if (separateItemOption) {
                separateItemOption.value = String(Number(separateItemOption.value) + 1)
            }
            htmx.trigger(document.querySelector('[data-cart]'), 'refresh')
            setTimeout(() => openCart(), 1000)
            addToCart.innerHTML = 'Add to Cart'
            addToCart.removeAttribute('disabled')
        });
    }

    // Re-query the quote form in case it was loaded via Sprig
    const raqFormElement = document.querySelector('.product-quote-modal form');
    if (raqFormElement) {
        raqFormElement.addEventListener('onBeforeFormieSubmit', () => {
            updateRaqOptions()
        })
    }

        /**
            * Initialize available product options only if material options exist
        */
        // Only when there is no configurator and no dimension inputs
        if (materialOptions.length) {
            updateAdditionalOptions(variantInitialId)
        }

    }
}

/**
 * Initialize all product form functionality
 * Call this after Sprig/HTMX loads the sidebar
 */
function initAllProductForms() {
    console.log('[Product.js] Initializing all product forms...');

    // Re-initialize quantity inputs (they're loaded via Sprig now)
    initQuantityInputs();

    // Re-initialize modal triggers (for quote form buttons loaded via Sprig)
    initModalTriggers();

    // Initialize standard product form (variant selection, etc.)
    initProductForm();

    // Initialize dimension-based product (if applicable)
    initDimensionBasedProduct();
}

initAllProductForms();
// Wait for Sprig/HTMX to load the product sidebar before initializing
// Wait for HTMX to swap in the content
document.addEventListener('htmx:afterSwap', function(event) {
    console.log(event);
    console.log('[Product.js] Sidebar loaded via Sprig, initializing forms...');
    initAllProductForms();
});

/**
 * Product Quote Form Toggle (old)
 */
// showQuoteForm.addEventListener('click', (event) => {
//     event.preventDefault();
//     productDetail.classList.add('show-quote-form')
// })


/**
 * Initialize available product options only if material options exist
 * NOTE: This is now handled inside initProductForm() which is called after Sprig loads
 */
// if (!configurator) {
//     if (typeof materialOptions !== 'undefined' && materialOptions != null) {
//         if (materialOptions.length) {
//             updateAdditionalOptions(variantInitialId)
//         }
//     }
// }


/**
 * Initialize Dimension-Based Products (Configurator)
 * Wrapped in a function so it can be called after Sprig/HTMX loads the sidebar
 */
function initDimensionBasedProduct() {
    // Check if dimension inputs exist (for configurator products)
    const widthFeetInput = document.querySelector('[data-width-feet-input]');
    const widthInchesInput = document.querySelector('[data-width-inches-input]');
    const heightFeetInput = document.querySelector('[data-height-feet-input]');
    const heightInchesInput = document.querySelector('[data-height-inches-input]');
    const dimensionSelect = document.querySelector('[data-dimension-select]');
    const configuredWidthInput = document.querySelector('[data-configured-width-input]');
    const configuredHeightInput = document.querySelector('[data-configured-height-input]');

    // If dimension inputs don't exist, nothing to initialize
    if (!widthFeetInput && !widthInchesInput && !heightFeetInput && !heightInchesInput) {
        //console.log('[initDimensionBasedProduct] No dimension inputs found, skipping');
        return;
    }

    // console.log('[initDimensionBasedProduct] Initializing dimension-based product...');
    // console.log('[initDimensionBasedProduct] Found inputs:', {
    //     widthFeetInput: !!widthFeetInput,
    //     widthInchesInput: !!widthInchesInput,
    //     heightFeetInput: !!heightFeetInput,
    //     heightInchesInput: !!heightInchesInput,
    //     dimensionSelect: !!dimensionSelect,
    //     configuredWidthInput: !!configuredWidthInput,
    //     configuredHeightInput: !!configuredHeightInput
    // });

    // If dimension inputs exist, set up validation
    if (widthFeetInput || widthInchesInput || heightFeetInput || heightInchesInput) {
    //console.log('[initDimensionBasedProduct] Setting up dimension validation and event listeners...');
    // Min/max width and minimum order price from CMS (data on dimensions section)
    const dimensionsOption = document.querySelector('[data-dimensions-option]');
    const minWidthFeet = dimensionsOption ? parseFloat(dimensionsOption.dataset.minWidthFeet) || 5 : 5;
    const maxWidthFeet = dimensionsOption ? parseInt(dimensionsOption.dataset.maxWidthFeet, 10) || 50 : 50;
    const minimumOrderPrice = dimensionsOption ? parseFloat(dimensionsOption.dataset.minimumOrderPrice) || 92 : 92;
    const widthErrorEl = document.querySelector('[data-dimension-width-error]');

    // Track if we've calculated a price
    let priceCalculated = false;
    let lastCalculatedWidth = 0;
    let lastCalculatedHeight = 0;
    let lastCalculatedVariant = null;

    const showWidthError = (message) => {
        if (widthErrorEl) {
            widthErrorEl.textContent = message;
            widthErrorEl.style.display = 'block';
        }
    };
    const hideWidthError = () => {
        if (widthErrorEl) {
            widthErrorEl.textContent = '';
            widthErrorEl.style.display = 'none';
        }
    };
    const getTotalWidthInFeet = () => {
        const wf = parseInt(widthFeetInput?.value || 0) || 0;
        const wi = parseInt(widthInchesInput?.value || 0) || 0;
        return wf + wi / 12;
    };
    const isWidthValid = () => {
        const total = getTotalWidthInFeet();
        return total >= minWidthFeet && total <= maxWidthFeet;
    };
    const validateWidthAndShowError = () => {
        const total = getTotalWidthInFeet();
        if (total <= 0) {
            hideWidthError();
            return true; // allow empty / not yet filled
        }
        if (total < minWidthFeet) {
            showWidthError(`Minimum width is ${minWidthFeet} ft. Please enter at least ${minWidthFeet} ft.`);
            return false;
        }
        if (total > maxWidthFeet) {
            showWidthError(`Maximum width is ${maxWidthFeet} ft. Please enter no more than ${maxWidthFeet} ft.`);
            return false;
        }
        hideWidthError();
        return true;
    };

    const updateAddToCartDisabledState = () => {
        const btn = document.querySelector('[data-add-to-cart]');
        if (btn && dimensionsOption) {
            btn.disabled = !isWidthValid();
        }
    };

    // For configurator products, ensure both the purchase form and quote form are visible
    const productDetail = document.querySelector('[data-product-detail]');
    const summary = document.querySelector('.product-form__summary');
    const quoteForm = document.querySelector('.product-quote-form');

    // Only clear inline styles for configurator products (has-configurator class)
    // For quote-only products, CSS should handle visibility based on show-quote-form class
    if (productDetail && productDetail.classList.contains('has-configurator')) {
        // Don't remove show-quote-form class - CSS will handle showing both forms
        // Just ensure the forms themselves don't have inline styles preventing display
        if (summary) {
            summary.style.display = '';
        }
        if (quoteForm) {
            quoteForm.style.display = '';
        }
    }

    // Debug logging for quote-only products
    // console.log('[Product Init] Product detail classes:', productDetail?.className);
    // console.log('[Product Init] Quote form found:', !!quoteForm);
    // console.log('[Product Init] Summary found:', !!summary);

    // Validate and constrain input values for text inputs
    const validateDimensionInput = (input, max) => {
        // Remove any non-digit characters
        let cleanValue = input.value.replace(/[^\d]/g, '');

        // If empty, don't modify the input - just return
        if (cleanValue === '') {
            return;
        }

        let value = parseInt(cleanValue);

        // Constrain to max
        if (value > max) {
            value = max;
        }

        input.value = value.toString();
        return value;
    };

    // Prevent non-numeric input and values over max
    const handleDimensionKeypress = (e, max) => {
        const input = e.target;

        // Allow control keys
        if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
            return;
        }

        // Prevent non-numeric input
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
            return;
        }

        // Get what the new value would be
        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;
        const currentValue = input.value;
        const newValue = currentValue.substring(0, selectionStart) + e.key + currentValue.substring(selectionEnd);
        const newNumericValue = parseInt(newValue);

        // Check if the new value would exceed max
        if (newNumericValue > max) {
            e.preventDefault();
        }
    };

    // Validate total height doesn't exceed 9 feet (108 inches)
    const validateHeightTotal = () => {
        if (!heightFeetInput || !heightInchesInput) return;

        const feet = parseInt(heightFeetInput.value) || 0;
        const inches = parseInt(heightInchesInput.value) || 0;
        const totalInches = (feet * 12) + inches;

        // Maximum 9 feet = 108 inches
        if (totalInches > 108) {
            // Reduce to max
            heightFeetInput.value = '9';
            heightInchesInput.value = '0';
        }
    };

    // Match height to variant dimensions based on maxHeight
    const matchHeightToVariant = () => {
        if (!heightFeetInput || !heightInchesInput || !dimensionSelect) return;

        const feet = parseInt(heightFeetInput.value) || 0;
        const inches = parseInt(heightInchesInput.value) || 0;
        const totalInches = (feet * 12) + inches;

        //console.log('[matchHeightToVariant] Height:', feet, 'ft', inches, 'in', '=', totalInches, 'total inches');

        // Find the variant with the smallest maxHeight that is >= totalInches
        const options = Array.from(dimensionSelect.querySelectorAll('option[data-max-height]'));
        //console.log('[matchHeightToVariant] Available options:', options.length);

        let bestMatch = null;
        let smallestValidMaxHeight = Infinity;

        for (let option of options) {
            const maxHeightInFeet = parseFloat(option.dataset.maxHeight) || 0;
            const maxHeightInInches = maxHeightInFeet * 12; // Convert feet to inches
            //console.log('[matchHeightToVariant] Checking option:', option.dataset.name, 'maxHeight:', maxHeightInFeet, 'feet (', maxHeightInInches, 'inches)');

            // Check if this dimension's maxHeight can accommodate our height
            if (maxHeightInInches >= totalInches) {
                //console.log('[matchHeightToVariant]   ✓ This option can accommodate height', totalInches, 'inches');
                // Find the smallest maxHeight that still works
                if (maxHeightInInches < smallestValidMaxHeight) {
                    smallestValidMaxHeight = maxHeightInInches;
                    bestMatch = option;
                    //console.log('[matchHeightToVariant]   ✓ New best match:', option.dataset.name);
                }
            } else {
                //console.log('[matchHeightToVariant]   ✗ Height', totalInches, 'exceeds maxHeight', maxHeightInInches);
            }
        }

        // Set the matched dimension or keep current selection if no match
        if (bestMatch) {
            //console.log('[matchHeightToVariant] MATCHED:', bestMatch.dataset.name, 'value:', bestMatch.value);
            dimensionSelect.value = bestMatch.value;
        } else {
            //console.log('[matchHeightToVariant] NO MATCH FOUND - keeping current selection');
            // Don't clear the selection if we don't find a match
        }

        //console.log('[matchHeightToVariant] Final dimensionSelect value:', dimensionSelect.value);

        // Trigger change event for variant selection
        dimensionSelect.dispatchEvent(new Event('change'));

        // Update price after variant selection (force update to ensure it recalculates)
        updateConfiguratorPrice(true);
    };

    // Update hidden inputs with configured dimensions for cart submission
    const updateConfiguredDimensions = () => {
        if (!configuredWidthInput || !configuredHeightInput) {
            //console.warn('[updateConfiguredDimensions] Hidden inputs not found!', {
            //     configuredWidthInput,
            //     configuredHeightInput
            // });
            return;
        }

        // Calculate total width in feet (convert inches to decimal)
        const widthFeet = parseInt(widthFeetInput?.value || 0);
        const widthInches = parseInt(widthInchesInput?.value || 0);
        const totalWidthInFeet = widthFeet + (widthInches / 12);

        // Calculate total height in feet (convert inches to decimal)
        const heightFeet = parseInt(heightFeetInput?.value || 0);
        const heightInches = parseInt(heightInchesInput?.value || 0);
        const totalHeightInFeet = heightFeet + (heightInches / 12);

        // Update hidden inputs
        configuredWidthInput.value = totalWidthInFeet.toFixed(4);
        configuredHeightInput.value = totalHeightInFeet.toFixed(4);

        // console.log('[updateConfiguredDimensions] Updated hidden inputs:', {
        //     widthInputValue: configuredWidthInput.value,
        //     heightInputValue: configuredHeightInput.value,
        //     widthInputName: configuredWidthInput.name,
        //     heightInputName: configuredHeightInput.name
        // });
    };

    // Update initial per-foot price display when material/variant changes (before dimensions are entered)
    const updateInitialPerFootPrice = () => {
        const priceElement = document.querySelector('[data-price-element]');
        if (!priceElement) {
            return;
        }

        // Get selected variant price
        const variantSelector = document.querySelector('#purchasableId');
        const selectedOption = variantSelector?.querySelector('option:checked');
        if (!selectedOption) {
            return;
        }

        const basePrice = parseFloat(selectedOption.dataset.price) || 0;

        const formatPrice = (price) => {
            // Round to 2 decimal places before formatting to avoid floating-point precision issues
            const rounded = Math.round(price * 100) / 100;
            return '$' + rounded.toFixed(2);
        };

        // Only update if we have the per-foot suffix (meaning we're still in initial state)
        const perFootSuffix = priceElement.querySelector('[data-per-foot-suffix]');
        if (perFootSuffix) {
            // Update just the price part, keeping the suffix intact
            priceElement.childNodes[0].textContent = formatPrice(basePrice) + ' ';
        }
    };

    // Calculate and update price based on dimensions and selected variant
    const updateConfiguratorPrice = (forceUpdate = false) => {
        if (!widthFeetInput || !widthInchesInput || !heightFeetInput || !heightInchesInput) return;

        // Get width in feet (convert inches to decimal)
        // Use trim() to handle any whitespace and ensure we're reading the actual value
        const widthFeetValue = widthFeetInput.value.trim();
        const widthInchesValue = widthInchesInput.value.trim();
        const widthFeet = widthFeetValue === '' ? 0 : parseInt(widthFeetValue);
        const widthInches = widthInchesValue === '' ? 0 : parseInt(widthInchesValue);
        const totalWidthInFeet = widthFeet + (widthInches / 12);

        // Get height in feet (convert inches to decimal)
        const heightFeetValue = heightFeetInput.value.trim();
        const heightInchesValue = heightInchesInput.value.trim();
        const heightFeet = heightFeetValue === '' ? 0 : parseInt(heightFeetValue);
        const heightInches = heightInchesValue === '' ? 0 : parseInt(heightInchesValue);
        const totalHeightInFeet = heightFeet + (heightInches / 12);

        // Only calculate price if BOTH width and height have actual non-zero values
        // Check if the actual input VALUE exists and is not empty or "0"
        const hasWidth = (widthFeetValue !== '' && widthFeetValue !== '0') || (widthInchesValue !== '' && widthInchesValue !== '0');
        const hasHeight = (heightFeetValue !== '' && heightFeetValue !== '0') || (heightInchesValue !== '' && heightInchesValue !== '0');

        // Get price elements
        const priceElement = document.querySelector('[data-price-element]');
        const quotePriceElement = document.querySelector('[data-quote-price-element]');
        const salePriceElement = document.querySelector('[data-sale-price-element]');
        const quoteSalePriceElement = document.querySelector('[data-quote-sale-price-element]');
        const priceContainer = priceElement?.parentElement;
        const startingAtLabel = priceContainer?.querySelector('span:first-child');

        // If either dimension is missing or zero
        if (!hasWidth || !hasHeight) {
            // If we've already calculated a price, keep it (don't reset)
            if (priceCalculated) {
                return;
            }
            // If we haven't calculated a price yet, update the per-foot price if material changed
            updateInitialPerFootPrice();
            return;
        }

        // Get selected variant price
        const variantSelector = document.querySelector('#purchasableId');
        const selectedOption = variantSelector?.querySelector('option:checked');

        if (!selectedOption) return;

        const currentVariantId = selectedOption.value;

        // Check if dimensions OR variant have changed since last calculation
        // Skip this check if forceUpdate is true (for quantity changes or material changes)
        if (!forceUpdate && priceCalculated &&
            totalWidthInFeet === lastCalculatedWidth &&
            totalHeightInFeet === lastCalculatedHeight &&
            currentVariantId === lastCalculatedVariant) {
            // Nothing has changed, don't recalculate
            return;
        }

        // Store these dimensions and variant as the last calculated
        lastCalculatedWidth = totalWidthInFeet;
        lastCalculatedHeight = totalHeightInFeet;
        lastCalculatedVariant = currentVariantId;
        priceCalculated = true;

        const basePrice = parseFloat(selectedOption.dataset.price) || 0;
        const salePrice = parseFloat(selectedOption.dataset.salePrice) || 0;
        const quantity = parseInt(document.querySelector('[data-quantity-input]')?.value) || 1;

        // Calculate total: base price * width * quantity
        // Round price per item first, then multiply by quantity to maintain proper rounding
        const pricePerItem = Math.round(basePrice * 100 * totalWidthInFeet) / 100;
        const salePricePerItem = Math.round(salePrice * 100 * totalWidthInFeet) / 100;

        // Now multiply the rounded per-item price by quantity and round again
        let totalPrice = Math.round(pricePerItem * 100 * quantity) / 100;
        let totalSalePrice = Math.round(salePricePerItem * 100 * quantity) / 100;

        // Apply CMS-configured minimum order price (default 92)
        if (totalPrice < minimumOrderPrice) {
            totalPrice = minimumOrderPrice;
        }
        if (totalSalePrice < minimumOrderPrice) {
            totalSalePrice = minimumOrderPrice;
        }

        const formatPrice = (price) => {
            // Round to 2 decimal places before formatting to avoid floating-point precision issues
            const rounded = Math.round(price * 100) / 100;
            return '$' + rounded.toFixed(2);
        };

        // Update "Starting at:" label to "Total:" after calculation
        if (startingAtLabel && startingAtLabel.textContent.includes('Starting at')) {
            startingAtLabel.textContent = 'Total:';
        }

        // Remove "per width/ft" suffix when configured price is calculated
        const perFootSuffix = priceElement?.querySelector('[data-per-foot-suffix]');
        if (perFootSuffix) {
            perFootSuffix.remove();
        }

        if (basePrice === salePrice || salePrice === 0) {
            // No sale
            if (priceElement) {
                priceElement.innerHTML = formatPrice(totalPrice);
                priceElement.parentElement?.classList.remove('on-sale');
            }
            if (quotePriceElement) {
                quotePriceElement.innerHTML = formatPrice(totalPrice);
                quotePriceElement.parentElement?.classList.remove('on-sale');
            }
            if (salePriceElement) salePriceElement.innerHTML = '';
            if (quoteSalePriceElement) quoteSalePriceElement.innerHTML = '';
        } else {
            // On sale
            if (priceElement) {
                priceElement.innerHTML = formatPrice(totalPrice);
                priceElement.parentElement?.classList.add('on-sale');
            }
            if (quotePriceElement) {
                quotePriceElement.innerHTML = formatPrice(totalPrice);
                quotePriceElement.parentElement?.classList.add('on-sale');
            }
            if (salePriceElement) salePriceElement.innerHTML = formatPrice(totalSalePrice);
            if (quoteSalePriceElement) quoteSalePriceElement.innerHTML = formatPrice(totalSalePrice);
        }
    };

    // Add event listeners for validation and matching
    if (widthFeetInput) {
        widthFeetInput.addEventListener('keydown', (e) => handleDimensionKeypress(e, maxWidthFeet));
        widthFeetInput.addEventListener('input', () => {
            validateDimensionInput(widthFeetInput, maxWidthFeet);
            validateWidthAndShowError();
            updateAddToCartDisabledState();
            // Update hidden inputs for cart submission
            updateConfiguredDimensions();
            // Update price immediately on width change (force update to bypass change detection)
            updateConfiguratorPrice(true);
        });
        widthFeetInput.addEventListener('paste', (e) => {
            e.preventDefault();
            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            const value = parseInt(pastedText.replace(/[^\d]/g, '')) || 0;
            widthFeetInput.value = Math.min(value, maxWidthFeet).toString();
            validateWidthAndShowError();
            updateAddToCartDisabledState();
            // Update hidden inputs for cart submission
            updateConfiguredDimensions();
            // Update price after paste (force update to bypass change detection)
            updateConfiguratorPrice(true);
        });
    }

    if (widthInchesInput) {
        widthInchesInput.addEventListener('keydown', (e) => handleDimensionKeypress(e, 11));
        widthInchesInput.addEventListener('input', () => {
            validateDimensionInput(widthInchesInput, 11);
            validateWidthAndShowError();
            updateAddToCartDisabledState();
            // Update hidden inputs for cart submission
            updateConfiguredDimensions();
            // Update price immediately on width change (force update to bypass change detection)
            updateConfiguratorPrice(true);
        });
        widthInchesInput.addEventListener('paste', (e) => {
            e.preventDefault();
            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            const value = parseInt(pastedText.replace(/[^\d]/g, '')) || 0;
            widthInchesInput.value = Math.min(value, 11).toString();
            validateWidthAndShowError();
            updateAddToCartDisabledState();
            // Update hidden inputs for cart submission
            updateConfiguredDimensions();
            // Update price after paste (force update to bypass change detection)
            updateConfiguratorPrice(true);
        });
    }

    if (heightFeetInput) {
        heightFeetInput.addEventListener('keydown', (e) => handleDimensionKeypress(e, 50));
        heightFeetInput.addEventListener('input', () => {
            validateDimensionInput(heightFeetInput, 50);
            validateHeightTotal();
            // Update hidden inputs for cart submission
            updateConfiguredDimensions();
            // Match height to variant which will call updateConfiguratorPrice(true)
            matchHeightToVariant();
        });
        heightFeetInput.addEventListener('paste', (e) => {
            e.preventDefault();
            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            const value = parseInt(pastedText.replace(/[^\d]/g, '')) || 0;
            heightFeetInput.value = Math.min(value, 50).toString();
            validateHeightTotal();
            // Update hidden inputs for cart submission
            updateConfiguredDimensions();
            // Match height to variant which will call updateConfiguratorPrice(true)
            matchHeightToVariant();
        });
    }

    if (heightInchesInput) {
        heightInchesInput.addEventListener('keydown', (e) => handleDimensionKeypress(e, 11));
        heightInchesInput.addEventListener('input', () => {
            validateDimensionInput(heightInchesInput, 11);
            validateHeightTotal();
            // Update hidden inputs for cart submission
            updateConfiguredDimensions();
            // Match height to variant which will call updateConfiguratorPrice(true)
            matchHeightToVariant();
        });
        heightInchesInput.addEventListener('paste', (e) => {
            e.preventDefault();
            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            const value = parseInt(pastedText.replace(/[^\d]/g, '')) || 0;
            heightInchesInput.value = Math.min(value, 11).toString();
            validateHeightTotal();
            // Update hidden inputs for cart submission
            updateConfiguredDimensions();
            // Match height to variant which will call updateConfiguratorPrice(true)
            matchHeightToVariant();
        });
    }

    // Initial match on page load
    matchHeightToVariant();

    // Listen for quantity changes
    const quantityInput = document.querySelector('[data-quantity-input]');
    if (quantityInput) {
        quantityInput.addEventListener('change', () => updateConfiguratorPrice(true));
        quantityInput.addEventListener('input', () => updateConfiguratorPrice(true));
    }

    // For configurator products WITHOUT dimension inputs, handle quantity-based pricing
    if (!widthFeetInput && !heightFeetInput) {
        const variantSelector = document.querySelector('#purchasableId');
        const priceElement = document.querySelector('[data-price-element]');
        const salePriceElement = document.querySelector('[data-sale-price-element]');
        const quantityInput = document.querySelector('[data-quantity-input]');

        const updateSimpleConfiguratorPrice = () => {
            if (!variantSelector || !priceElement || !quantityInput) return;

            const selectedOption = variantSelector.querySelector('option:checked');
            if (!selectedOption) return;

            const basePrice = parseFloat(selectedOption.dataset.price) || 0;
            const salePrice = parseFloat(selectedOption.dataset.salePrice) || 0;
            const quantity = parseInt(quantityInput.value) || 1;

            // Calculate total price
            // To avoid floating-point precision issues, convert to cents, round, then convert back
            const totalPrice = Math.round(basePrice * 100 * quantity) / 100;
            const totalSalePrice = Math.round(salePrice * 100 * quantity) / 100;

            const formatPrice = (price) => {
                // Round to 2 decimal places before formatting to avoid floating-point precision issues
                const rounded = Math.round(price * 100) / 100;
                return '$' + rounded.toFixed(2);
            };

            // Update label from "Starting at:" to "Total:"
            const priceContainer = priceElement.parentElement;
            const startingAtLabel = priceContainer?.querySelector('span:first-child');
            if (startingAtLabel && startingAtLabel.textContent.includes('Starting at')) {
                startingAtLabel.textContent = 'Total:';
            }

            if (basePrice === salePrice || salePrice === 0) {
                // No sale
                priceElement.innerHTML = formatPrice(totalPrice);
                priceElement.parentElement?.classList.remove('on-sale');
                if (salePriceElement) salePriceElement.innerHTML = '';
            } else {
                // On sale
                priceElement.innerHTML = formatPrice(totalPrice);
                priceElement.parentElement?.classList.add('on-sale');
                if (salePriceElement) salePriceElement.innerHTML = formatPrice(totalSalePrice);
            }
        };

        if (quantityInput) {
            quantityInput.addEventListener('change', updateSimpleConfiguratorPrice);
            quantityInput.addEventListener('input', updateSimpleConfiguratorPrice);
        }

        // Initial price calculation
        updateSimpleConfiguratorPrice();
    }

    // Listen for material/variant changes
    const variantSelector = document.querySelector('#purchasableId');
    if (variantSelector) {
        variantSelector.addEventListener('change', () => updateConfiguratorPrice(true));
    }

    // Listen for dimension changes to update the variant selector
    if (dimensionSelect) {
        dimensionSelect.addEventListener('change', () => {
            // Get current material/color selection
            const material = document.querySelector('[name="material"]:checked')?.value ?? '';
            const color = document.querySelector('[name="color"]:checked')?.value ?? '';
            const dimensions = dimensionSelect.value;

            // Find the matching variant that matches BOTH material/color AND dimension
            const matchingOption = Array.from(variantSelector.querySelectorAll('option')).find(option => {
                const materialMatch = !material || option.dataset.material === material;
                const colorMatch = !color || option.dataset.color === color;
                const dimensionMatch = option.dataset.dimensions === dimensions;

                return (materialMatch || colorMatch) && dimensionMatch;
            });

            if (matchingOption) {
                variantSelector.value = matchingOption.value;
                // Trigger change event on variant selector to update price
                variantSelector.dispatchEvent(new Event('change'));
            }
        });
    }

    // For configurator products, we need to handle option changes to update the variant selector
    // This mirrors the logic from the non-configurator section but adapted for configurators
    const configuratorProductForm = document.querySelector('[data-product-form]');
    if (configuratorProductForm) {
        const materialOptions = configuratorProductForm.querySelectorAll('[data-material-option] input[type="radio"]');
        const colorOptions = configuratorProductForm.querySelectorAll('[data-color-option] input[type="radio"]');
        const productOptionGroups = configuratorProductForm.querySelectorAll('[data-product-option]');

        // Handle material toggle changes (if product has material groups)
        materialOptions.forEach((option, index) => {
            option.addEventListener('change', (event) => {
                const groupId = event.target.dataset.group;
                const swatchGroups = document.querySelectorAll('[data-material-group]');
                const selectedColor = document.querySelector('[name="material"]:checked');

                // de-select any selected colors
                if (selectedColor) {
                    selectedColor.checked = false;
                }

                // toggle colors displayed and auto-select first color
                swatchGroups.forEach(groupDiv => {
                    if (groupDiv.dataset.materialGroup == groupId) {
                        groupDiv.classList.remove("--is-hidden");
                        const firstInput = document.querySelector(`.swatch-options[data-material-group="${groupId}"]`).querySelectorAll('input')[0];
                        if (firstInput) {
                            firstInput.click(); // This will trigger the color change handler which updates the variant
                        }
                    } else {
                        groupDiv.classList.add("--is-hidden");
                    }
                });
            });
        });

        colorOptions.forEach(colorOption => {
            colorOption.addEventListener('change', (event) => {
                // Update the color label
                const colorOptionContainer = configuratorProductForm.querySelector('[data-color-option]');
                const titleEl = colorOptionContainer?.querySelector('[data-option-title]');
                const noteInput = document.querySelector('input[data-selected-color]');
                const colorName = event.target.dataset.name;
                if (colorName) {
                    if (titleEl) {
                        titleEl.innerHTML = colorName;
                    }
                    if (noteInput) {
                        noteInput.value = colorName;
                    }
                }

                // Get all current selections
                const material = document.querySelector('[name="material"]:checked')?.value ?? '';
                const color = document.querySelector('[name="color"]:checked')?.value ?? '';
                const dimensions = dimensionSelect?.value ?? '';

                // Find ALL matching variants, then select the cheapest one
                const matchingOptions = Array.from(variantSelector.querySelectorAll('option')).filter(option => {
                    // Match based on material OR color (depending on product type)
                    const hasMaterialAttr = option.dataset.material !== undefined && option.dataset.material !== '';
                    const hasColorAttr = option.dataset.color !== undefined && option.dataset.color !== '';

                    let materialMatch = false;
                    if (hasMaterialAttr && material) {
                        materialMatch = option.dataset.material === material;
                    } else if (hasColorAttr && color) {
                        materialMatch = option.dataset.color === color;
                    }

                    const dimensionMatch = !dimensions || option.dataset.dimensions === dimensions;

                    return materialMatch && dimensionMatch;
                });

                if (matchingOptions.length > 0) {
                    // Find the cheapest variant from matching options
                    const cheapestOption = matchingOptions.reduce((cheapest, current) => {
                        const cheapestPrice = parseFloat(cheapest.dataset.price) || Infinity;
                        const currentPrice = parseFloat(current.dataset.price) || Infinity;
                        return currentPrice < cheapestPrice ? current : cheapest;
                    }, matchingOptions[0]);

                    variantSelector.value = cheapestOption.value;
                    variantSelector.dispatchEvent(new Event('change'));
                }
            });
        });
    }

    // Initial price calculation
    updateConfiguratorPrice();

    // Set Add to Cart button disabled state based on width validation (e.g. disabled when width < 5 ft)
    updateAddToCartDisabledState();

    // Add form submit handler for configurator products
    // Note: quantityInput, variantSelector, and configuratorProductForm are already declared above
    const configuratorAddToCart = document.querySelector('[data-add-to-cart]');
    const configuratorColorOption = document.querySelector('[name="options[color]"]') ?? '';

    if (configuratorProductForm && configuratorAddToCart) {
        // Check if we've already attached the listener (prevent duplicates)
        if (!configuratorProductForm.dataset.submitHandlerAttached) {
            configuratorProductForm.dataset.submitHandlerAttached = 'true';

            configuratorProductForm.addEventListener('submit', async function(event) {
                event.preventDefault();

                if (!isWidthValid()) {
                    validateWidthAndShowError();
                    if (widthErrorEl) {
                        widthErrorEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                    return;
                }

                configuratorAddToCart.setAttribute('disabled', 'disabled');
                configuratorAddToCart.innerHTML = 'Adding...';

                const purchasableId = variantSelector.value;

                if (purchasableId) {
                    const csrfToken = await getCsrfToken({ force: true });

                    // Build options object
                    const options = {};
                    if (configuratorColorOption && configuratorColorOption.value) {
                        options.color = configuratorColorOption.value;
                    }
                    // Add configured dimensions
                    if (configuredWidthInput && configuredWidthInput.value) {
                        options.configuredWidth = configuredWidthInput.value;
                    }
                    if (configuredHeightInput && configuredHeightInput.value) {
                        options.configuredHeight = configuredHeightInput.value;
                    }

                    const response = await client.post('', {
                        action: "commerce/cart/update-cart",
                        purchasableId,
                        options,
                        qty: quantityInput.value
                    }, {
                        headers: {
                            'X-CSRF-Token': csrfToken
                        }
                    }).catch(error => console.error(error));
                }

                htmx.trigger(document.querySelector('[data-cart]'), 'refresh');
                setTimeout(() => openCart(), 1000);
                configuratorAddToCart.innerHTML = 'Add to Cart';
                configuratorAddToCart.removeAttribute('disabled');
            });
        }
    }
    }
}

/**
 * Configurator Page Functionality
 */
if (configurator) {
    // Dynamically adds and removes a body class to style the configurator for print. See product.scss for the styles.
    function handleConfiguratorPrint() {
        // Append link element to head
        document.body.classList.add('print-configurator');

        // Remove the stylesheet after printing or if print is cancelled
        const removePrintStylesheet = () => {
            document.body.classList.remove('print-configurator');

            // window.removeEventListener('beforeprint', removePrintStylesheet);
            window.removeEventListener('afterprint', removePrintStylesheet);
        };

        // window.addEventListener('beforeprint', removePrintStylesheet);
        window.addEventListener('afterprint', removePrintStylesheet);

        // Trigger print dialog
        window.print();
    }

    async function handleConfiguratorEvent(eventType, eventDetail) {
        const {
            configurationId,
            configuratorVariant,
            configurationName,
            configurationJson,
            configurationPanelScreenshots,
            configurationAssets,
            commerceProduct,
            productTypes,
            quote
        } = eventDetail;

        console.log('Configurator: Event Detail', eventDetail);

        try {
            const csrfToken = await getCsrfToken({ force: true });

            // Save and login actions use the same endpoint
            const action = eventType === 'save' || eventType === 'login' || eventType === 'saveQuote'
                ? 'configurator/configurator/save-data'
                : 'configurator/configurator/generate-pdf';

            const payload = {
                action,
                configuratorVariant,
                configurationJson,
                configurationPanelScreenshots,
                configurationAssets,
                commerceProduct,
                productTypes,
            };

            // Add optional data for save actions
            if (eventType === 'save') {
                payload.configurationTitle = configurationName;
            }

            // Add data for saveQuote actions
            if (eventType === 'saveQuote') {
                payload.quote = quote;
            }

            // Add optional data for pdf actions
            if (eventType === 'pdf') {
                payload.configurationId = configurationId;
            }

            const response = await client.post('', payload, {
                headers: {
                    'X-CSRF-Token': csrfToken
                }
            });

            // Handle save reponse
            if (eventType === 'save') {
                console.log(`Configurator: Saved successfully`);
            }

            // Handle saveQuote reponse
            if (eventType === 'saveQuote') {
                console.log(`Configurator: Saved successfully for quote`);
            }

            // Handle login reponse
            if (eventType === 'login') {
                console.log(`Configurator: Saved successfully for login`);

                const responseData = response?.data?.data;
                const configuratorId = responseData?.entryId;
                const configuratorJson = responseData?.configuratorJson;

                let redirectUrl = `/login?redirect=${window.location.pathname}`; // Set up redirect back to the current product page

                if (configuratorId && configuratorJson) {
                    sessionStorage.setItem(TEMP_CONFIGURATION_PARAM, configuratorJson); // Save the configurator JSON to sessionStorage temporarily
                    redirectUrl += `&${TEMP_CONFIGURATION_PARAM}=${configuratorId}`;
                } else {
                    console.error('Configurator: No configurator ID received:', response?.data);
                }

                // Redirect to login page
                window.location.href = redirectUrl;
            }

            // Handle PDF reponse
            if (eventType === 'pdf') {
                console.log(`Configurator: PDF generated or returned successfully`);

                const pdfUrl = response?.data?.data?.pdfUrl; // Not a typo, the PDF URL is nested
                if (pdfUrl) {
                    // Option 1: Open in new tab
                    window.open(pdfUrl, '_blank');
                    
                    // Option 2: Trigger download
                    // const link = document.createElement('a');
                    // link.href = pdfUrl;
                    // link.download = '';
                    // link.click();
                } else {
                    console.error('Configurator: No PDF URL received:', response.data);
                }
            }

            return response;
        } catch (error) {
            // Handle save error
            if (eventType === 'save') {
                console.error(`Configurator: Error saving:`, error);
            }

            // Handle saveQuote error
            if (eventType === 'saveQuote') {
                console.error(`Configurator: Error saving for quote:`, error);
            }

            // Handle login error
            if (eventType === 'login') {
                console.error(`Configurator: Error saving for login:`, error);
            }

            // Handle PDF error
            if (eventType === 'pdf') {
                console.error(`Configurator: Error generating PDF:`, error);
            }
            throw error;
        }
    }

    async function handleConfiguratorSave(event) {
        // console.log('Configurator: Save Event', event);
        const { saveId, ...otherDetails } = event.detail;
    
        try {
            await handleConfiguratorEvent('save', otherDetails);
    
            // Dispatch a completion event with the save ID
            window.dispatchEvent(new CustomEvent('configuratorSaveComplete', {
                detail: { success: true, id: saveId },
            }));
        } catch (error) {
            window.dispatchEvent(new CustomEvent('configuratorSaveComplete', {
                detail: { success: false, id: saveId, error },
            }));
        }
    }

    async function handleConfiguratorSaveQuote(event) {
        // console.log('Configurator: Save Quote Event', event);
        const { saveId, ...otherDetails } = event.detail;
    
        try {
            const result = await handleConfiguratorEvent('saveQuote', otherDetails)
    
            // Dispatch a completion event with the save ID and result
            window.dispatchEvent(new CustomEvent('configuratorSaveQuoteComplete', {
                detail: { success: true, id: saveId, result },
            }));
        } catch (error) {
            window.dispatchEvent(new CustomEvent('configuratorSaveQuoteComplete', {
                detail: { success: false, id: saveId, error },
            }));
        }
    }
    
    async function handleConfiguratorLogin(event) {
        // console.log('Configurator: Login Event', event);
        await handleConfiguratorEvent('login', event.detail);
    }
    
    async function handleConfiguratorDownloadProof(event) {
        // console.log('Configurator: Download Proof Event', event);
        await handleConfiguratorEvent('pdf', event.detail);
    }

    // Listen for the Configurator custom events
    window.addEventListener(configuratorPrintEvent, handleConfiguratorPrint);
    window.addEventListener(configuratorSaveEvent, handleConfiguratorSave);
    window.addEventListener(configuratorSaveQuoteEvent, handleConfiguratorSaveQuote);
    window.addEventListener(configuratorLoginEvent, handleConfiguratorLogin);
    window.addEventListener(configuratorDownloadProofEvent, handleConfiguratorDownloadProof);
}
